import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { limparRascunhoAgendamento } from '../utils/agendamentoStorage';

export interface StepData {
  step: number;
  employeesData?: any[];
  examsData?: any;
  schedulingData?: any;
  finalizationData?: any;
}

const STEPS = {
  1: '/agendamento/cadastro', // Cadastro de colaboradores
  2: '/agendamento/resumo', // Resumo
  3: '/agendamento/final', // Finalizado
};

const STEP_NAMES = {
  1: 'Cadastro',
  2: 'Resumo',
  3: 'Finalizado',
};

export function useStepNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  // Derivar passo atual diretamente da URL para evitar renderizações em cascata
  const stepEntry = Object.entries(STEPS).find(([, path]) => path === location.pathname);
  const currentStep = stepEntry ? parseInt(stepEntry[0]) : 1;

  const [stepData, setStepData] = useState<StepData>(() => {
    try {
      const savedEmpId = localStorage.getItem('customer_employeesData_empresa_id');
      const currentEmpId = localStorage.getItem('customer_empresa_id');
      const savedEmployees = localStorage.getItem('customer_employeesData');

      if (savedEmployees && (!savedEmpId || (currentEmpId && savedEmpId !== currentEmpId))) {
        limparRascunhoAgendamento();
        return { step: 1, employeesData: [] };
      }

      const saved = localStorage.getItem('customer_stepData');
      let parsedData: StepData = { step: 1 };

      if (saved) {
        parsedData = JSON.parse(saved);
      }

      if (savedEmployees) {
        try {
          const parsedEmployees = JSON.parse(savedEmployees);
          if (Array.isArray(parsedEmployees) && parsedEmployees.length > 0) {
            parsedData.employeesData = parsedEmployees;
          }
        } catch (e) {
          void e;
        }
      } else if (!parsedData.employeesData) {
        parsedData.employeesData = [];
      }

      if (!parsedData.examsData) {
        const savedExams = localStorage.getItem('customer_examsData');
        if (savedExams) parsedData.examsData = JSON.parse(savedExams);
      }

      if (!parsedData.schedulingData) {
        const savedScheduling = localStorage.getItem('customer_schedulingData');
        if (savedScheduling) parsedData.schedulingData = JSON.parse(savedScheduling);
      }

      if (!parsedData.finalizationData) {
        const savedFinalization = localStorage.getItem('customer_finalizationData');
        if (savedFinalization) parsedData.finalizationData = JSON.parse(savedFinalization);
      }

      return parsedData;
    } catch {
      return { step: 1 };
    }
  });

  const loadStepData = useCallback(() => {
    try {
      const savedEmpId = localStorage.getItem('customer_employeesData_empresa_id');
      const currentEmpId = localStorage.getItem('customer_empresa_id');
      const savedEmployees = localStorage.getItem('customer_employeesData');

      if (savedEmployees && (!savedEmpId || (currentEmpId && savedEmpId !== currentEmpId))) {
        limparRascunhoAgendamento();
        setStepData({ step: 1, employeesData: [] });
        return;
      }

      const saved = localStorage.getItem('customer_stepData');
      let parsedData: StepData = { step: 1 };

      if (saved) {
        parsedData = JSON.parse(saved);
      }

      if (savedEmployees) {
        try {
          const parsedEmployees = JSON.parse(savedEmployees);
          if (Array.isArray(parsedEmployees) && parsedEmployees.length > 0) {
            parsedData.employeesData = parsedEmployees;
          }
        } catch (e) {
          void e;
        }
      } else if (!parsedData.employeesData) {
        parsedData.employeesData = [];
      }

      if (!parsedData.examsData) {
        const savedExams = localStorage.getItem('customer_examsData');
        if (savedExams) parsedData.examsData = JSON.parse(savedExams);
      }

      if (!parsedData.schedulingData) {
        const savedScheduling = localStorage.getItem('customer_schedulingData');
        if (savedScheduling) parsedData.schedulingData = JSON.parse(savedScheduling);
      }

      if (!parsedData.finalizationData) {
        const savedFinalization = localStorage.getItem('customer_finalizationData');
        if (savedFinalization) parsedData.finalizationData = JSON.parse(savedFinalization);
      }

      setStepData(parsedData);
    } catch (error) {
      console.error('❌ Erro ao carregar dados dos passos:', error);
    }
  }, []);

  const saveStepData = (data: Partial<StepData>) => {
    const newData = { ...stepData, ...data, step: currentStep };
    setStepData(newData);

    // Salvar no localStorage com prefixo customer_ para não conflitar com scheduling
    localStorage.setItem('customer_stepData', JSON.stringify(newData));

    // Atualizar dados específicos de cada passo
    if (data.employeesData) {
      localStorage.setItem('customer_employeesData', JSON.stringify(data.employeesData));
      const currentEmpId = localStorage.getItem('customer_empresa_id');
      if (currentEmpId) {
        localStorage.setItem('customer_employeesData_empresa_id', currentEmpId);
      }
    }
    if (data.examsData) {
      localStorage.setItem('customer_examsData', JSON.stringify(data.examsData));
    }
    if (data.schedulingData) {
      localStorage.setItem('customer_schedulingData', JSON.stringify(data.schedulingData));
    }
    if (data.finalizationData) {
      localStorage.setItem('customer_finalizationData', JSON.stringify(data.finalizationData));
    }
  };

  const goToStep = (step: number) => {
    const path = STEPS[step as keyof typeof STEPS];
    if (path) {
      navigate(path);
    }
  };

  const goToNextStep = () => {
    const nextStep = currentStep + 1;
    console.log('🔄 [useStepNavigation] goToNextStep chamado', {
      currentStep,
      nextStep,
      stepData: {
        employeesData: stepData.employeesData?.length || 0,
        finalizationData: !!stepData.finalizationData,
        finalizationDataContent: stepData.finalizationData
      }
    });

    if (nextStep <= 3) {
      // Recarregar stepData do localStorage antes de verificar
      // Isso garante que temos os dados mais recentes
      loadStepData();
      
      // Pequeno delay para garantir que o estado foi atualizado
      setTimeout(() => {
        const canGo = canGoToStep(nextStep);
        console.log('🔍 [useStepNavigation] canGoToStep resultado:', canGo);
        
        if (canGo) {
          console.log('✅ [useStepNavigation] Avançando para o passo', nextStep);
          goToStep(nextStep);
        } else {
          console.warn('⚠️ [useStepNavigation] Não é possível avançar para o passo', nextStep, '- validação falhou');
          console.log('📊 [useStepNavigation] Estado atual completo:', {
            currentStep,
            nextStep,
            stepData: JSON.parse(JSON.stringify(stepData)),
            localStorage_stepData: localStorage.getItem('customer_stepData'),
            localStorage_finalizationData: localStorage.getItem('customer_finalizationData')
          });
        }
      }, 50);
    } else {
      console.warn('⚠️ [useStepNavigation] Já está no último passo');
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  const canGoToStep = (step: number): boolean => {
    // Validações básicas para permitir navegação
    switch (step) {
      case 2: // Resumo - precisa ter colaboradores
        // Verificar tanto estado quanto localStorage
        if (stepData.employeesData?.length) return true;
        try {
          const savedEmployees = localStorage.getItem('customer_employeesData');
          return !!(savedEmployees && JSON.parse(savedEmployees).length);
        } catch {
          return false;
        }
        
      case 3: // Finalizado - precisa ter dados de finalização
        // Verificar tanto estado quanto localStorage
        if (stepData.finalizationData) return true;
        try {
          const savedFinalization = localStorage.getItem('customer_finalizationData');
          return !!(savedFinalization && JSON.parse(savedFinalization));
        } catch {
          return false;
        }
        
      default:
        return true;
    }
  };

  const resetAllData = () => {
    setStepData({ step: 1 });
    limparRascunhoAgendamento();
  };

  return {
    currentStep,
    stepData,
    stepName: STEP_NAMES[currentStep as keyof typeof STEP_NAMES],
    saveStepData,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canGoToStep,
    resetAllData,
    loadStepData,
  };
}


